import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { useTheme } from '@/contexts/ThemeContext';
import { cn } from '@/lib/utils';
import { useIsMobile } from '@/hooks/use-mobile';
import { ArrowRight } from 'lucide-react';
import { contentfulVideoService } from '@/services/contentfulVideoService';
import { useQuery } from '@tanstack/react-query';
import VideoPreviewCard from '@/components/video/VideoPreviewCard';
import { Content } from '@/types/content';
const FeaturedVideosSection: React.FC = () => {
  const navigate = useNavigate();
  const {
    resolvedTheme
  } = useTheme();
  const isMobile = useIsMobile();
  const [isInView, setIsInView] = React.useState(false);
  const sectionRef = React.useRef<HTMLDivElement>(null);

  // Intersection observer to trigger autoplay when section comes into view
  React.useEffect(() => {
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry.isIntersecting) {
        setIsInView(true);
      }
    }, {
      threshold: 0.3
    } // Trigger when 30% of section is visible
    );
    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }
    return () => observer.disconnect();
  }, []);

  // Always use white text regardless of theme since background is dark
  const textColor = "text-white";
  const mutedTextColor = "text-white/70";

  // Use Contentful video service to fetch videos
  const {
    data: videoCategories = []
  } = useQuery({
    queryKey: ['videoCategories'],
    queryFn: contentfulVideoService.getVideoCategories,
    staleTime: 5 * 60 * 1000 // 5 minutes
  });

  // Get all videos to find the latest upload
  const {
    data: allVideos = []
  } = useQuery({
    queryKey: ['allVideos'],
    queryFn: contentfulVideoService.getVideos,
    staleTime: 5 * 60 * 1000 // 5 minutes
  });

  // Get featured video
  const {
    data: featuredVideos = []
  } = useQuery({
    queryKey: ['featuredVideos'],
    queryFn: contentfulVideoService.getFeaturedVideos,
    staleTime: 5 * 60 * 1000 // 5 minutes
  });

  // Find Mediterranean and Architecture categories
  const mediterraneanCategory = videoCategories.find(category => category.name.toLowerCase().includes('mediterranean'));
  const architectureCategory = videoCategories.find(category => category.name.toLowerCase().includes('architecture'));

  // Get one video from each category
  const mediterraneanVideo = mediterraneanCategory?.contents?.[0];
  const architectureVideo = architectureCategory?.contents?.[0];

  // Get the latest video upload (most recent based on sys.createdAt or releaseDate)
  const latestVideo = allVideos.length > 0 ? allVideos.sort((a, b) => {
    const dateA = new Date(a.releaseDate || '1970-01-01').getTime();
    const dateB = new Date(b.releaseDate || '1970-01-01').getTime();
    return dateB - dateA; // Sort descending (newest first)
  })[0] : null;

  // Handle navigation to video pages
  const handleVideoClick = (video: Content) => {
    navigate(`/videos/${video.slug || video.id}`);
  };
  return <div className="w-full bg-stone-800">
      <div ref={sectionRef} className="py-[44px] bg-black">
        <div className="max-w-screen-2xl mx-auto px-4 md:px-8 lg:px-12 xl:px-16">
          <div className="flex items-center justify-between mb-6">
            <h2 className={cn("text-2xl md:text-3xl font-chroma", textColor)}>Featured Videos</h2>
            <Button variant="ghost" onClick={() => navigate('/videos')} className="flex items-center text-white hover:text-black">
              View All <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </div>
          
          {/* Desktop Layout */}
          <div className={cn("hidden md:grid grid-cols-3 gap-6 lg:gap-8")}>
            {/* Main featured video - Latest Upload */}
            {latestVideo && <div className="col-span-2 cursor-pointer" onClick={() => handleVideoClick(latestVideo)}>
                <VideoPreviewCard video={latestVideo} aspectRatio="16/9" className="mb-3" autoplayOnLoad={isInView} previewDuration={30} />
                <div>
                  <h3 className={cn("text-xl font-chroma", textColor)}>
                    {latestVideo.title}
                  </h3>
                  <p className={cn("mt-1", mutedTextColor)}>
                    {latestVideo.description}
                  </p>
                </div>
              </div>}
            
            {/* Right column with 2 smaller videos */}
            <div className="space-y-6">
              {/* Mediterranean video */}
              {mediterraneanVideo && <div className="cursor-pointer" onClick={() => handleVideoClick(mediterraneanVideo)}>
                  <VideoPreviewCard video={mediterraneanVideo} aspectRatio="16/9" className="mb-2" />
                  <h3 className={cn("text-base font-chroma", textColor)}>
                    {mediterraneanVideo.title}
                  </h3>
                </div>}
              
              {/* Architecture video */}
              {architectureVideo && <div className="cursor-pointer" onClick={() => handleVideoClick(architectureVideo)}>
                  <VideoPreviewCard video={architectureVideo} aspectRatio="16/9" className="mb-2" />
                  <h3 className={cn("text-base font-chroma", textColor)}>
                    {architectureVideo.title}
                  </h3>
                </div>}
            </div>
          </div>
          
          {/* Mobile Layout */}
          <div className={cn("md:hidden space-y-6")}>
            {/* Main featured video */}
            {latestVideo && <div className="cursor-pointer" onClick={() => handleVideoClick(latestVideo)}>
                <VideoPreviewCard video={latestVideo} aspectRatio="16/9" className="mb-3" previewDelay={1200} autoplayOnLoad={isInView} previewDuration={30} />
                <h3 className={cn("text-lg font-chroma", textColor)}>
                  {latestVideo.title}
                </h3>
                <p className={cn("mt-1 text-sm", mutedTextColor)}>
                  {latestVideo.description?.substring(0, 80)}
                  {latestVideo.description?.length > 80 ? '...' : ''}
                </p>
              </div>}
            
            {/* Two smaller videos in a row */}
            <div className="grid grid-cols-2 gap-3">
              {/* Mediterranean video */}
              {mediterraneanVideo && <div className="cursor-pointer" onClick={() => handleVideoClick(mediterraneanVideo)}>
                  <VideoPreviewCard video={mediterraneanVideo} aspectRatio="16/9" className="mb-2" previewDelay={1200} />
                  <h3 className={cn("text-xs font-chroma", textColor)}>
                    {mediterraneanVideo.title}
                  </h3>
                </div>}
              
              {/* Architecture video */}
              {architectureVideo && <div className="cursor-pointer" onClick={() => handleVideoClick(architectureVideo)}>
                  <VideoPreviewCard video={architectureVideo} aspectRatio="16/9" className="mb-2" previewDelay={1200} />
                  <h3 className={cn("text-xs font-chroma", textColor)}>
                    {architectureVideo.title}
                  </h3>
                </div>}
            </div>
          </div>
        </div>
      </div>
    </div>;
};
export default FeaturedVideosSection;